import { useQueryClient } from '@tanstack/react-query'
import type {
  Checklist,
  ChecklistItemOptionsItem,
  UpsertChecklistRequest,
} from '@transreport/pauk-rrms-api'
import {
  getShowAssistanceChecklistQueryKey,
  useReplaceAssistanceChecklist,
  useShowAssistanceChecklist,
} from '@transreport/pauk-rrms-api'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppButton, Modal } from '@/components/framework'
import { trackEvent, trackOutcome } from '@/lib/analytics'
import type { ChecklistRequest } from '@/types/timeline'

import { ChecklistFormSkeleton } from './ChecklistFormSkeleton'

interface CompleteChecklistModalProps {
  isOpen: boolean
  onClose: () => void
  request: ChecklistRequest | null
  area: 'timeline' | 'requests'
}

interface ChecklistQuestionState {
  questionId: string
  checklistLabel: string
  type: string
  selectedOption: string | null
  options: ChecklistItemOptionsItem[]
}

const shouldUseWideQuestionLayout = (question: ChecklistQuestionState): boolean =>
  question.type === 'textarea' || question.type === 'checkbox'

const normalizeOptionValue = (value: string | null | undefined) =>
  (value ?? '').toString().trim().toLowerCase()

const normalizeLabel = (value: string | null | undefined) => (value ?? '').toString().trim()

const getQuestionControlId = (questionId: string) =>
  `checklist-question-${questionId.replace(/[^a-zA-Z0-9_-]/g, '-')}`

const OPTION_VALUE_KEY_BY_NORMALIZED_VALUE: Record<string, string> = {
  yes: 'checklistModal.optionsByValue.yes',
  no: 'checklistModal.optionsByValue.no',
  cancelled: 'checklistModal.optionsByValue.cancelled',
  atl: 'checklistModal.optionsByValue.atl',
  fcl: 'checklistModal.optionsByValue.fcl',
  no_show: 'checklistModal.optionsByValue.noShow',
}

type ChecklistUiModel = {
  /** Question IDs that should not be rendered in the main list */
  hiddenFromMainQuestionIds: Set<string>
  /** Question IDs that should be disabled in the UI */
  disabledQuestionIds: Set<string>
  /** Questions rendered in the arrival-time section (not in main list) */
  arrivalTimeSection: { isVisible: boolean; questionIndices: number[] }
  /** Question IDs that should have their value cleared (null) */
  shouldClearQuestionIds: Set<string>
}

const buildChecklistUiModel = (
  questions: ChecklistQuestionState[],
  isDepartureBookingType: boolean
): ChecklistUiModel => {
  // NOTE: the API currently does not expose stable "roles" for these questions, so we locate them
  // once here and keep all conditional UI rules centralized (instead of scattering label checks).
  const trainArrivalTimeIndex = questions.findIndex(
    (q) => q.checklistLabel === 'Train Arrival Time'
  )
  const timeAssistedIndex = questions.findIndex((q) => q.checklistLabel === 'Time Assisted')
  const checkedInIndex = questions.findIndex((q) => q.checklistLabel === 'Checked in')
  const metWithinFiveIndex = questions.findIndex((q) => q.checklistLabel === 'Met within 5 mins')
  const calledDestinationIndex = questions.findIndex(
    (q) => q.checklistLabel === 'Called destination station'
  )
  // DEP checklist uses "Time at ATL / FCL"; ARR may use "Time at Assisted Travel Lounge".
  const timeAtlIndex = questions.findIndex((q) =>
    isDepartureBookingType
      ? q.checklistLabel.includes('Time at ATL / FCL')
      : q.checklistLabel.includes('Time at Assisted Travel Lounge')
  )

  const checkedInValue =
    checkedInIndex >= 0 ? normalizeOptionValue(questions[checkedInIndex]?.selectedOption) : ''
  const isCheckedInNoShow = checkedInValue === 'no_show'
  const enableTimeAtl = !isDepartureBookingType || ['atl', 'fcl'].includes(checkedInValue)

  const hiddenFromMainQuestionIds = new Set<string>()
  const disabledQuestionIds = new Set<string>()
  const shouldClearQuestionIds = new Set<string>()

  // Arrival time section: keep questions in state/payload but render separately.
  const arrivalTimeSectionQuestionIndices: number[] = []
  if (!isDepartureBookingType && trainArrivalTimeIndex >= 0 && timeAssistedIndex >= 0) {
    arrivalTimeSectionQuestionIndices.push(trainArrivalTimeIndex, timeAssistedIndex)
    hiddenFromMainQuestionIds.add(questions[trainArrivalTimeIndex].questionId)
    hiddenFromMainQuestionIds.add(questions[timeAssistedIndex].questionId)
  }

  // “Met within 5 mins” follow-up textarea (assumed to be next question).
  const metWithinFiveValue =
    metWithinFiveIndex >= 0
      ? normalizeOptionValue(questions[metWithinFiveIndex]?.selectedOption)
      : ''
  if (metWithinFiveValue === 'yes') {
    const followUp = questions[metWithinFiveIndex + 1]
    if (followUp?.type === 'textarea') {
      hiddenFromMainQuestionIds.add(followUp.questionId)
      shouldClearQuestionIds.add(followUp.questionId)
    }
  }

  // “Called destination station” follow-up textarea (assumed to be next question).
  const calledDestinationValue =
    calledDestinationIndex >= 0
      ? normalizeOptionValue(questions[calledDestinationIndex]?.selectedOption)
      : ''
  if (calledDestinationValue === 'yes') {
    const followUp = questions[calledDestinationIndex + 1]
    if (followUp?.type === 'textarea') {
      hiddenFromMainQuestionIds.add(followUp.questionId)
      shouldClearQuestionIds.add(followUp.questionId)
    }
  }

  // DEP checklist: hide "Time at ATL / FCL" when Checked in is No Show; disable when ATL/FCL not selected.
  if (timeAtlIndex >= 0) {
    const timeAtlQuestionId = questions[timeAtlIndex].questionId
    if (isDepartureBookingType && isCheckedInNoShow) {
      hiddenFromMainQuestionIds.add(timeAtlQuestionId)
      shouldClearQuestionIds.add(timeAtlQuestionId)
    } else if (!enableTimeAtl) {
      disabledQuestionIds.add(timeAtlQuestionId)
      shouldClearQuestionIds.add(timeAtlQuestionId)
    }
  }

  return {
    hiddenFromMainQuestionIds,
    disabledQuestionIds,
    arrivalTimeSection: {
      isVisible: !isDepartureBookingType && arrivalTimeSectionQuestionIndices.length === 2,
      questionIndices: arrivalTimeSectionQuestionIndices,
    },
    shouldClearQuestionIds,
  }
}

const mapChecklistQuestions = (checklist: Checklist | undefined): ChecklistQuestionState[] =>
  checklist?.items.map((item) => ({
    questionId: item.question_id,
    checklistLabel: item.text,
    type: item.option_type,
    selectedOption: item.selected_option,
    options: item.options ?? [],
  })) ?? []

const buildChecklistPayload = (
  questionStates: ChecklistQuestionState[],
  ui: ChecklistUiModel
): UpsertChecklistRequest => ({
  answers: questionStates.map((answer) => ({
    question_id: answer.questionId,
    selected_option: ui.disabledQuestionIds.has(answer.questionId) ? null : answer.selectedOption,
  })),
})

export const CompleteChecklistModal: React.FC<CompleteChecklistModalProps> = ({
  isOpen,
  onClose,
  request,
  area,
}) => {
  const { t } = useTranslation()

  const getQuestionLabel = (question: ChecklistQuestionState) => {
    const raw = normalizeLabel(question.checklistLabel)
    return t(`checklistModal.questionsById.${question.questionId}`, { defaultValue: raw })
  }

  const getOptionLabel = (value: string, fallbackText: string) => {
    const rawFallback = normalizeLabel(fallbackText)
    const key = OPTION_VALUE_KEY_BY_NORMALIZED_VALUE[normalizeOptionValue(value)]
    return key ? t(key, { defaultValue: rawFallback }) : rawFallback
  }

  const queryClient = useQueryClient()
  const [questions, setQuestions] = useState<ChecklistQuestionState[]>([])

  const assistanceId = request?.id ?? ''
  const isDepartureBookingType = request?.type === 'DEP'

  const checklistQuery = useShowAssistanceChecklist(assistanceId, {
    query: {
      enabled: isOpen && Boolean(assistanceId),
      staleTime: 60_000,
    },
  })

  const replaceChecklistMutation = useReplaceAssistanceChecklist({
    mutation: {
      meta: {
        toast: {
          success: t('checklistModal.saveSuccess'),
          error: t('checklistModal.saveError'),
        },
      },
    },
  })

  useEffect(() => {
    if (isOpen) trackEvent('checklist_started', { area })
  }, [area, isOpen])

  useEffect(() => {
    if (!isOpen) {
      setQuestions([])
      return
    }

    if (!checklistQuery.data) return

    setQuestions(mapChecklistQuestions(checklistQuery.data))
  }, [checklistQuery.data, isOpen])

  const isSaving = replaceChecklistMutation.isPending
  const isColdLoading = checklistQuery.isPending && !checklistQuery.data
  const uiModel = useMemo(
    () => buildChecklistUiModel(questions, isDepartureBookingType),
    [questions, isDepartureBookingType]
  )

  const updateQuestionValue = (index: number, value: string | null) => {
    setQuestions((prev) => {
      const next = prev.map((question, questionIndex) =>
        questionIndex === index ? { ...question, selectedOption: value } : question
      )

      const nextUi = buildChecklistUiModel(next, isDepartureBookingType)
      if (nextUi.shouldClearQuestionIds.size === 0) return next

      return next.map((q) =>
        nextUi.shouldClearQuestionIds.has(q.questionId) ? { ...q, selectedOption: null } : q
      )
    })
  }

  const handleClose = () => {
    if (isSaving) return
    setQuestions([])
    onClose()
  }

  const handleConfirm = async () => {
    if (!assistanceId || questions.length === 0 || isSaving) return

    const payload = buildChecklistPayload(questions, uiModel)
    const checklistQueryKey = getShowAssistanceChecklistQueryKey(assistanceId)

    const stationContext = trackOutcome('checklist_completed', area, 'attempted')
    try {
      const savedChecklist = await replaceChecklistMutation.mutateAsync({
        id: assistanceId,
        data: payload,
      })

      queryClient.setQueryData(checklistQueryKey, savedChecklist)

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: checklistQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['shiftTimeline'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboardRequests'] }),
      ])

      trackOutcome('checklist_completed', area, 'succeeded', stationContext)
      handleClose()
    } catch {
      trackOutcome('checklist_completed', area, 'failed', {
        ...stationContext,
        failure_type: 'api',
      })
      // The global mutation handler displays the error.
    }
  }

  const renderQuestionInput = (question: ChecklistQuestionState, index: number) => {
    const controlId = getQuestionControlId(question.questionId)

    if (question.type === 'radio') {
      return (
        <div className="flex flex-wrap gap-4">
          {question.options.map((option, optionIndex) => (
            <label
              key={option.value}
              htmlFor={`${controlId}-${optionIndex}`}
              className="inline-flex items-center gap-3 text-base text-gray-800"
            >
              <input
                id={`${controlId}-${optionIndex}`}
                type="radio"
                name={`checklist-radio-${question.questionId}`}
                value={option.value}
                checked={question.selectedOption === option.value}
                onChange={(event) => updateQuestionValue(index, event.target.value)}
                className="size-5 border-border-gray text-primary focus:ring-primary"
              />
              <span>{getOptionLabel(option.value, option.text)}</span>
            </label>
          ))}
        </div>
      )
    }

    if (question.type === 'dropdown') {
      return (
        <select
          id={controlId}
          value={question.selectedOption ?? ''}
          onChange={(event) => updateQuestionValue(index, event.target.value || null)}
          className="w-full rounded-input border border-border-gray px-4 py-2.5 text-base"
        >
          <option value="">{t('checklistModal.selectPlaceholder')}</option>
          {question.options.map((option) => (
            <option key={option.value} value={option.value}>
              {getOptionLabel(option.value, option.text)}
            </option>
          ))}
        </select>
      )
    }

    if (question.type === 'checkbox') {
      return (
        <div className="flex flex-wrap gap-4">
          {question.options.map((option, optionIndex) => (
            <label
              key={option.value}
              htmlFor={`${controlId}-${optionIndex}`}
              className="inline-flex items-center gap-3 text-base text-gray-800"
            >
              <input
                id={`${controlId}-${optionIndex}`}
                type="checkbox"
                checked={question.selectedOption === option.value}
                onChange={() =>
                  updateQuestionValue(
                    index,
                    question.selectedOption === option.value ? null : option.value
                  )
                }
                className="size-5 rounded border-border-gray text-primary focus:ring-primary"
              />
              <span>{getOptionLabel(option.value, option.text)}</span>
            </label>
          ))}
        </div>
      )
    }

    if (question.type === 'textarea') {
      return (
        <textarea
          id={controlId}
          value={question.selectedOption ?? ''}
          onChange={(event) => updateQuestionValue(index, event.target.value)}
          rows={4}
          className="w-full rounded-input border border-border-gray px-4 py-3 text-base"
          placeholder={t('checklistModal.textareaPlaceholder')}
        />
      )
    }

    if (question.type === 'time') {
      const isDisabled = uiModel.disabledQuestionIds.has(question.questionId)
      return (
        <input
          id={controlId}
          type="time"
          value={question.selectedOption ?? ''}
          onChange={(event) => updateQuestionValue(index, event.target.value || null)}
          disabled={isDisabled}
          className="w-full rounded-input border border-border-gray px-4 py-2.5 text-base disabled:bg-gray-100"
        />
      )
    }

    return (
      <input
        id={controlId}
        type="text"
        value={question.selectedOption ?? ''}
        onChange={(event) => updateQuestionValue(index, event.target.value)}
        className="w-full rounded-input border border-border-gray px-4 py-2.5 text-base"
      />
    )
  }

  if (!request) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isDepartureBookingType ? t('checklistModal.depTitle') : t('checklistModal.arrTitle')}
      size="xl"
    >
      <div className="space-y-4" aria-busy={isColdLoading || isSaving}>
        {isColdLoading && <ChecklistFormSkeleton loadingLabel={t('checklistModal.loading')} />}

        {!!checklistQuery.error && (
          <p
            role="alert"
            className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {t('checklistModal.loadError')}
          </p>
        )}

        {checklistQuery.isSuccess && questions.length === 0 && (
          <p className="text-sm text-gray-700">{t('checklistModal.noQuestions')}</p>
        )}

        {!isColdLoading && (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            {uiModel.arrivalTimeSection.isVisible && (
              <section className="rounded bg-gray-50 p-4">
                <div className="mb-3 text-sm font-semibold text-gray-900">
                  {t('checklistModal.arrivalSectionTitle')}
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {uiModel.arrivalTimeSection.questionIndices.map((index) => {
                    const question = questions[index]

                    return (
                      <div key={question.questionId}>
                        <label
                          htmlFor={getQuestionControlId(question.questionId)}
                          className="mb-2 block text-base font-semibold text-gray-900"
                        >
                          {getQuestionLabel(question)}
                        </label>
                        {renderQuestionInput(question, index)}
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            <section className="rounded bg-white p-4">
              <div className="space-y-4">
                {questions.map((question, index) => {
                  if (uiModel.hiddenFromMainQuestionIds.has(question.questionId)) return null

                  const isWide = shouldUseWideQuestionLayout(question)
                  const questionRowClass = `grid grid-cols-1 gap-3 border-b border-gray-100 pb-4 last:border-b-0 last:pb-0 ${isWide ? 'md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]' : 'md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] md:items-center'}`

                  if (question.type === 'radio' || question.type === 'checkbox') {
                    return (
                      <fieldset key={question.questionId} className={questionRowClass}>
                        <legend className="text-base font-semibold text-gray-900">
                          {getQuestionLabel(question)}
                        </legend>
                        <div>{renderQuestionInput(question, index)}</div>
                      </fieldset>
                    )
                  }

                  return (
                    <div key={question.questionId} className={questionRowClass}>
                      <label
                        htmlFor={getQuestionControlId(question.questionId)}
                        className="text-base font-semibold text-gray-900"
                      >
                        {getQuestionLabel(question)}
                      </label>
                      <div>{renderQuestionInput(question, index)}</div>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>
        )}

        {!isColdLoading && (
          <div className="sticky bottom-0 z-10 -mx-modal-padding mt-1 flex justify-end gap-gap-sm border-t border-border-lighter bg-white px-modal-padding pt-3 pb-0.5">
            <AppButton
              variant="primary"
              size="md"
              onClick={handleConfirm}
              disabled={isSaving}
              loading={isSaving}
            >
              {t('checklistModal.confirm')}
            </AppButton>
            <AppButton variant="secondary" size="md" onClick={handleClose} disabled={isSaving}>
              {t('checklistModal.cancel')}
            </AppButton>
          </div>
        )}
      </div>
    </Modal>
  )
}
